import {
  use,
  useRef,
  useState,
  type CSSProperties,
  type InputEvent,
} from "react";
import clsx from "clsx";

import { getConfig } from "lib/get-config";
import { useTranslation } from "lib/i18n/use-translation";
import { NavigationContext } from "lib/navigation/use-navigation";
import { usePaths } from "lib/navigation/use-paths";
import { useToc } from "lib/toc/use-toc";

import css from "./scrubber.module.scss";

type TooltipState = {
  show: boolean;
  text: string;
  position: number;
  scrubberWidth: number;
};

export function Scrubber() {
  const { page, totalPages, goToPage } = use(NavigationContext)!;
  const { toc } = useToc();
  const { trials } = getConfig();
  const { pageToXpointer, xpointerToChapter } = usePaths();
  const { t } = useTranslation();

  const isFragment = trials === 1;

  const fragmentData = isFragment && toc?.["fb3-fragment"];
  const fragmentRatio = fragmentData
    ? fragmentData.fragment_length / fragmentData.full_length
    : null;
  const max = totalPages > 0 ? totalPages : 100;
  const effectiveMax = fragmentRatio ? Math.ceil(max / fragmentRatio) : max;
  const [tooltip, setTooltip] = useState<TooltipState>({
    show: false,
    text: "",
    position: 0,
    scrubberWidth: 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [previewPage, setPreviewPage] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function computeTooltip(input: HTMLInputElement): TooltipState {
    const value = Number(input.value);
    const hoveredPage = value - 1;

    const xp = pageToXpointer(hoveredPage);
    const chapterTitle = xp ? (xpointerToChapter(xp)?.t ?? null) : null;

    const rect = input.getBoundingClientRect();
    const percentage = ((value - 1) / (effectiveMax - 1)) * 100;
    const position = (rect.width * percentage) / 100;

    return {
      show: true,
      text: chapterTitle || `Page ${value}`,
      position,
      scrubberWidth: rect.width,
    };
  }

  function onInput(event: InputEvent<HTMLInputElement>) {
    const target = event.currentTarget as HTMLInputElement;

    if (isDragging) {
      setPreviewPage(Number(target.value) - 1);
    } else {
      // Клавиатурная навигация: input меняет value стрелками,
      // но контролируемый value сбрасывает его обратно — поэтому
      // явно прыгаем на нужную страницу.
      goToPage(Number(target.value) - 1);
    }

    setTooltip(computeTooltip(target));
  }

  function onMouseDown() {
    setIsDragging(true);
    if (inputRef.current) {
      setTooltip(computeTooltip(inputRef.current));
    }
  }

  function onMouseUp() {
    if (previewPage !== null) {
      goToPage(previewPage);
    }
    setIsDragging(false);
    setPreviewPage(null);
    setTooltip(prev => ({ ...prev, show: false }));
  }

  function onMouseLeave() {
    setTooltip(prev => ({ ...prev, show: false }));
  }

  const displayPage = isDragging && previewPage !== null ? previewPage : page;
  const progress =
    effectiveMax > 1 ? (displayPage / (effectiveMax - 1)) * 100 : 0;

  return (
    <div className={css.root}>
      <input
        ref={inputRef}
        className={clsx(css.input, isFragment && css.fragment)}
        type="range"
        min="1"
        max={effectiveMax}
        value={displayPage + 1}
        onInput={onInput}
        // иначе React сбросит значение input до обновления state
        onChange={() => {}}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onTouchStart={onMouseDown}
        onTouchEnd={onMouseUp}
        data-testid="readerProgressBar"
        aria-label={t("Прогресс чтения")}
        aria-valuetext={t("Страница {{page}} из {{total}}", {
          page: displayPage + 1,
          total: effectiveMax,
        })}
        style={{
          ["--scrubber-progress" as keyof CSSProperties]: `${progress}%`,
          ["--fragment-ratio" as keyof CSSProperties]: fragmentRatio
            ? `${fragmentRatio * 100}%`
            : "100%",
        }}
      />
      {tooltip.show && (
        <div
          className={css.tooltip}
          style={{
            ["--tooltip-position" as keyof CSSProperties]: `${tooltip.position}px`,
            ["--scrubber-width" as keyof CSSProperties]: `${tooltip.scrubberWidth}px`,
          }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
}
